import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { RefreshCw as RefreshIcon } from 'lucide-react';
import { Button as UiButton, Chip, CoordioDropdown, FormField, ShellHeader, Textarea, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@coordio/ui';
import { EmptyState, InlineErrorPanel, LoadingSkeleton, PageContainer } from '@coordio/ui/layout';
import { CortexDialog, CortexDialogBody, CortexDialogFooter } from '../components/dialogs/CortexDialog';
import { memoryModuleService } from '../services/memoryModuleService';
import { useNotificationStore } from '../store/notificationStore';
import type { AgentMemoryDefinitionDto, ExtractionOutboxDto } from '../types/memoryModule';
import { OUTCOME_SOURCES, SESSION_OUTCOMES } from '../types/memoryModule';
import DataTable, { type DataTableColumn } from '../components/data-display/DataTable';
import { ROUTES } from '../utils/routes';

/** Chip colour per outbox state — the write path's state machine (Memory spec §5.3). */
const stateChip = (row: ExtractionOutboxDto) => {
  switch (row.outboxState) {
    case 'Pending':
      return <Chip label="Pending" />;
    case 'Deferred':
      return (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <span><Chip variant="outlined" label="Deferred" /></span>
            </TooltipTrigger>
            <TooltipContent>{row.deferredUntilUtc ? `Waiting for outcome until ${new Date(row.deferredUntilUtc).toLocaleString()}` : 'Waiting for outcome binding'}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    case 'Claimed':
      return <Chip label="Claimed" />;
    case 'Extracted':
      return <Chip label="Extracted" />;
    case 'Skipped':
      return <Chip variant="outlined" label="Skipped" />;
    case 'Failed':
      return <Chip label="Failed" />;
    default:
      return <Chip variant="outlined" label={row.outboxState} />;
  }
};

/**
 * The Memory Pipeline page (Phase 3): per-definition view of the extraction outbox — session
 * close → gates → extraction. States and reasons only; the transcript never appears here (the
 * session link is the way to a transcript, under its own authorisation).
 */
export const MemoryPipelinePage = () => {
  const navigate = useNavigate();
  const { showNotification } = useNotificationStore();

  const [definitions, setDefinitions] = useState<AgentMemoryDefinitionDto[]>([]);
  const [selectedDef, setSelectedDef] = useState<AgentMemoryDefinitionDto | null>(null);
  const [rows, setRows] = useState<ExtractionOutboxDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [outcomeRow, setOutcomeRow] = useState<ExtractionOutboxDto | null>(null);
  const [outcome, setOutcome] = useState('Completed');
  const [source, setSource] = useState('UserConfirmed');
  const [failureReason, setFailureReason] = useState('');
  const [savingOutcome, setSavingOutcome] = useState(false);

  useEffect(() => {
    memoryModuleService
      .listDefinitions()
      .then((defs) => {
        setDefinitions(defs);
        if (defs.length > 0) setSelectedDef(defs[0]);
        else setLoading(false);
      })
      .catch((err: any) => {
        setError(err.response?.data?.message || 'Failed to load memory definitions');
        setLoading(false);
      });
  }, []);

  const loadRows = useCallback(async (definitionId: string) => {
    setLoading(true);
    setError(null);
    try {
      setRows(await memoryModuleService.listOutbox(definitionId));
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load the extraction outbox');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedDef) loadRows(selectedDef.agentMemoryDefinitionId);
  }, [selectedDef, loadRows]);

  const columns: DataTableColumn<ExtractionOutboxDto>[] = [
    {
      id: 'outboxId',
      label: '#',
      minWidth: 60,
      align: 'right',
      render: (r) => r.outboxId,
    },
    {
      id: 'state',
      label: 'State',
      minWidth: 110,
      render: (r) => stateChip(r),
    },
    {
      id: 'session',
      label: 'Session',
      minWidth: 130,
      render: (r) => (
        <button className="text-sm text-[var(--color-accent-primary)] hover:underline" onClick={() => navigate(`/sessions/${r.agentSessionId}`)}>
          {r.agentSessionId.slice(0, 8)}…
        </button>
      ),
    },
    {
      id: 'scope',
      label: 'Scope',
      minWidth: 220,
      render: (r) => (
        <div className="flex flex-wrap gap-0.5">
          {r.scope.length === 0 ? (
            <span className="text-xs text-[var(--color-text-secondary)]">—</span>
          ) : (
            r.scope.map((p) => (
              <Chip key={p.key} variant="outlined" label={`${p.key}: ${p.value}`} />
            ))
          )}
        </div>
      ),
    },
    {
      id: 'signals',
      label: 'Signals',
      minWidth: 130,
      render: (r) => (
        <div className="flex gap-0.5">
          {r.hadToolError && <Chip variant="outlined" label="Tool error" />}
          {r.hadUserCorrection && <Chip variant="outlined" label="Corrected" />}
          {r.isHumanFlagged && <Chip variant="outlined" label="Flagged" />}
          {r.selfAssessedConcern && <Chip variant="outlined" label="Self-assessed" />}
          {r.isEvalHoldout && <Chip variant="outlined" label="Holdout" />}
          {!r.hadToolError && !r.hadUserCorrection && !r.isHumanFlagged && !r.selfAssessedConcern && !r.isEvalHoldout && (
            <span className="text-xs text-[var(--color-text-secondary)]">—</span>
          )}
        </div>
      ),
    },
    {
      id: 'outcome',
      label: 'Outcome',
      minWidth: 160,
      render: (r) => (
        <div className="flex items-center gap-1">
          <p className="text-sm">{r.outcome}</p>
          <button
            className="text-sm text-[var(--color-accent-primary)] hover:underline"
            onClick={() => {
              setOutcomeRow(r);
              setOutcome(r.outcome && r.outcome !== 'Unknown' ? r.outcome : 'Completed');
              setSource('UserConfirmed');
              setFailureReason('');
            }}
          >
            Set
          </button>
        </div>
      ),
    },
    {
      id: 'qa',
      label: 'QA',
      minWidth: 140,
      render: (r) =>
        r.qaStratum ? (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span><Chip variant="outlined" label={r.qaStratum} /></span>
              </TooltipTrigger>
              <TooltipContent>{r.pQa != null ? `p_qa ${r.pQa.toFixed(2)}` : 'QA stratum'}</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ) : (
          <span className="text-xs text-[var(--color-text-secondary)]">—</span>
        ),
    },
    {
      id: 'reason',
      label: 'Reason',
      minWidth: 220,
      render: (r) => {
        const reason = r.samplingStratum || r.lastError;
        return reason ? (
          <span className="text-xs">{reason}</span>
        ) : (
          <span className="text-xs text-[var(--color-text-secondary)]">—</span>
        );
      },
    },
    {
      id: 'probabilities',
      label: 'p(admit) / p(extract)',
      minWidth: 150,
      align: 'right',
      render: (r) => `${r.pAdmit.toFixed(2)} / ${r.pExtract.toFixed(2)}`,
    },
    {
      id: 'createdAtUtc',
      label: 'Queued',
      minWidth: 160,
      render: (r) => new Date(r.createdAtUtc).toLocaleString(),
    },
    {
      id: 'processedAtUtc',
      label: 'Processed',
      minWidth: 160,
      render: (r) => (r.processedAtUtc ? new Date(r.processedAtUtc).toLocaleString() : '—'),
    },
  ];

  return (
    <>
      <ShellHeader
        title="Memory Pipeline"
        data-testid="memory-pipeline-header"
        description="Extraction outbox per Agent Memory Definition — states and reasons only."
        actions={
          <UiButton
            variant="outline"
            disabled={!selectedDef || loading}
            onClick={() => selectedDef && loadRows(selectedDef.agentMemoryDefinitionId)}
            data-testid="memory-pipeline-refresh-btn"
          >
            <RefreshIcon /> Refresh
          </UiButton>
        }
      />

      <PageContainer data-testid="memory-pipeline-form-page">
        {error && (
          <InlineErrorPanel
            message={error}
            data-testid="memory-pipeline-error-panel"
            action={<UiButton variant="ghost" size="sm" onClick={() => setError(null)}>Dismiss</UiButton>}
          />
        )}

        {loading && definitions.length === 0 ? (
          <LoadingSkeleton rows={6} data-testid="memory-pipeline-loading" />
        ) : !loading && definitions.length === 0 ? (
          <EmptyState
            title="No memory definitions"
            description="The pipeline is per Agent Memory Definition. Create a definition first."
            action={<UiButton variant="default" size="sm" onClick={() => navigate(ROUTES.MEMORY_DEFINITIONS)}>Memory Definitions</UiButton>}
          />
        ) : (
          <>
            {definitions.length > 0 && (
              <div className="mb-4 max-w-[480px]">
                <FormField label="Agent Memory Definition" description="Select the definition whose extraction outbox you want to inspect.">
                  <CoordioDropdown
                    options={definitions.map((d) => ({
                      value: d.agentMemoryDefinitionId,
                      label: `${d.displayName} (${d.agentKey})`,
                    }))}
                    value={selectedDef?.agentMemoryDefinitionId ?? null}
                    onChange={(val) => {
                      const next = definitions.find((d) => d.agentMemoryDefinitionId === val);
                      if (next) setSelectedDef(next);
                    }}
                    clearable={false}
                    data-testid="memory-pipeline-definition-selector"
                  />
                </FormField>
              </div>
            )}

            {loading ? (
              <LoadingSkeleton rows={4} />
            ) : rows.length === 0 ? (
              <EmptyState
                title="No outbox entries"
                description="Nothing queued for this definition yet — complete a scoped session to enqueue one."
              />
            ) : (
              <DataTable
                columns={columns}
                rows={rows}
                loading={loading}
                emptyMessage="Nothing queued for this definition yet."
              />
            )}
          </>
        )}
      </PageContainer>

      <CortexDialog
        open={outcomeRow !== null}
        onClose={() => setOutcomeRow(null)}
        title="Set memory outcome"
        size="sm"
        data-testid="memory-pipeline-outcome-dialog"
      >
        <CortexDialogBody>
          <div className="flex flex-col gap-4">
            <FormField label="Outcome" description="The session outcome classification.">
              <CoordioDropdown
                options={SESSION_OUTCOMES.map((o) => ({ value: o, label: o }))}
                value={outcome}
                onChange={(val) => setOutcome(val ?? '')}
                data-testid="memory-pipeline-outcome"
              />
            </FormField>
            <FormField label="Source" description="How the outcome was determined.">
              <CoordioDropdown
                options={OUTCOME_SOURCES.map((s) => ({ value: s, label: s }))}
                value={source}
                onChange={(val) => setSource(val ?? '')}
                data-testid="memory-pipeline-source"
              />
            </FormField>
            {outcome === 'Incorrect' && (
              <FormField label="Failure reason" description="Explain why the session outcome was incorrect.">
                <Textarea
                  className="w-full"
                  value={failureReason}
                  onChange={(e) => setFailureReason(e.target.value)}
                  rows={2}
                  data-testid="memory-pipeline-failureReason"
                />
              </FormField>
            )}
          </div>
        </CortexDialogBody>
        <CortexDialogFooter>
          <UiButton variant="outline" onClick={() => setOutcomeRow(null)} disabled={savingOutcome} data-testid="cancel-memory-pipeline-btn">
            Cancel
          </UiButton>
          <UiButton
            disabled={savingOutcome || !outcomeRow}
            data-testid="save-memory-pipeline-btn"
            onClick={async () => {
              if (!outcomeRow) return;
              setSavingOutcome(true);
              try {
                const receipt = await memoryModuleService.assignSessionOutcome(outcomeRow.agentSessionId, {
                  outcome,
                  outcomeSource: source,
                  failureReason: outcome === 'Incorrect' ? failureReason.trim() || undefined : undefined,
                });
                const parts = [`Outcome set to ${receipt.outcome} (rev ${receipt.outcomeRevisionCount})`];
                if (receipt.forcedExtraction) parts.push('extraction reopened');
                showNotification(parts.join(' — '), 'success');
                setOutcomeRow(null);
                if (selectedDef) await loadRows(selectedDef.agentMemoryDefinitionId);
              } catch (err: any) {
                showNotification(err.response?.data?.message || err.message || 'Failed to set outcome', 'error');
              } finally {
                setSavingOutcome(false);
              }
            }}
          >
            {savingOutcome ? 'Saving…' : 'Save'}
          </UiButton>
        </CortexDialogFooter>
      </CortexDialog>
    </>
  );
};
